import { useMemo, useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'

import currencies from '../../../consts/currencies'
import languages from '../../../consts/languages'
import { ActionSelections, SelectionOption } from '../../../core/types/chat.types'
import FlowActionInlineForm from './flow-action-inline-form'

type FlowActionLangCurrencyFormProps = {
  title: string
  onCancel: () => void
  onApply: (selections: ActionSelections) => void
}

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'grid',
    gap: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'var(--ds-fg-secondary, #535b6c)',
  },
  select: {
    width: '100%',
    borderRadius: 10,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    color: 'var(--ds-fg-primary, #1f2430)',
    padding: theme.spacing(1.25, 1.5),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    outline: 'none',
  },
}))

const getOptionLabel = (option: SelectionOption): string =>
  option.description || option.label || option.value || option.code || ''

const FlowActionLangCurrencyForm = ({
  title,
  onCancel,
  onApply,
}: FlowActionLangCurrencyFormProps) => {
  const classes = useStyles()
  const [selectedLanguage, setSelectedLanguage] = useState('EN')
  const [selectedCurrency, setSelectedCurrency] = useState('USD')

  const languageOptions = useMemo(() => languages, [])
  const currencyOptions = useMemo(() => currencies, [])

  const handleApply = () => {
    const translationLanguage = languageOptions.find((option) => option.code === selectedLanguage)
    const conversionChosen = currencyOptions.find((option) => option.code === selectedCurrency)

    if (!translationLanguage || !conversionChosen) {
      return
    }

    onApply({
      translationLanguage,
      conversionChosen,
    })
  }

  return (
    <FlowActionInlineForm title={title} canApply onCancel={onCancel} onApply={handleApply}>
      <div className={classes.grid}>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="flow-language">
            Language
          </label>
          <select
            id="flow-language"
            className={classes.select}
            value={selectedLanguage}
            onChange={(event) => setSelectedLanguage(event.target.value)}
          >
            {languageOptions.map((option) => (
              <option key={option.code || option.value} value={option.code || option.value || ''}>
                {getOptionLabel(option)}
              </option>
            ))}
          </select>
        </div>

        <div className={classes.field}>
          <label className={classes.label} htmlFor="flow-currency">
            Currency
          </label>
          <select
            id="flow-currency"
            className={classes.select}
            value={selectedCurrency}
            onChange={(event) => setSelectedCurrency(event.target.value)}
          >
            {currencyOptions.map((option) => (
              <option key={option.code || option.value} value={option.code || option.value || ''}>
                {getOptionLabel(option)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </FlowActionInlineForm>
  )
}

export default FlowActionLangCurrencyForm
